'use client'

import {
    createSubPlan,
    getResaleMinimumPrice,
    getResaleParentModules,
    getSubPlan,
    getSubPlanRegistrationLinks,
    createSubPlanRegistrationLink,
    revokeSubPlanRegistrationLink,
    SubPlanData,
    updateSubPlan,
} from '@/lib/api/resales/resales-api'
import RegistrationLinksPanel from '@/components/registration-links/registration-links-panel'
import {
    getSystemModules,
    SystemModule,
} from '@/lib/api/system-modules/system-modules-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdExpandLess,
    MdExpandMore,
    MdExtension,
    MdStorefront,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const currencyFormat = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}
const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    return parseInt(unMask(value, 'currency')) || 0
}
const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (!cents) return ''
    return currencyFormat(cents.toString())
}

type AllocationKey =
    | 'users'
    | 'teleconsultationMinutes'
    | 'monthlySignDocuments'
    | 'monthlyFiscalNotes'

const ALLOCATION_FIELDS: {
    key: AllocationKey
    label: string
}[] = [
    { key: 'users', label: 'Usuários' },
    { key: 'teleconsultationMinutes', label: 'Minutos de Teleconsulta' },
    { key: 'monthlySignDocuments', label: 'Documentos para Assinatura/Mês' },
    { key: 'monthlyFiscalNotes', label: 'Notas Fiscais/Mês' },
]

export default function ResaleFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingPage, setLoadingPage] = useState(true)

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [price, setPrice] = useState('')
    const [recurrence, setRecurrence] = useState('MONTHLY')
    const [allocations, setAllocations] = useState<
        Record<AllocationKey, string>
    >({
        users: '',
        teleconsultationMinutes: '',
        monthlySignDocuments: '',
        monthlyFiscalNotes: '',
    })

    // Preço mínimo do plano (centavos), calculado no backend — o front não conhece
    // multiplicador nem preço unitário.
    const [minPriceCents, setMinPriceCents] = useState(0)
    const [systemModules, setSystemModules] = useState<SystemModule[]>([])
    // Módulos/recursos liberados pelo plano principal (pai). chave=módulo,
    // valor=array de resources permitidos ([] = todos os resources do módulo).
    const [parentModules, setParentModules] = useState<Record<string, string[]>>(
        {}
    )
    const [selectedModules, setSelectedModules] = useState<
        Record<string, string[]>
    >({})
    const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set())

    useEffect(() => {
        void init()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const init = async () => {
        setLoadingPage(true)
        try {
            const [parentRes, modulesRes] = await Promise.all([
                getResaleParentModules(),
                getSystemModules(),
            ])
            const parent =
                parentRes.success && parentRes.result ? parentRes.result : {}
            setParentModules(parent)
            if (modulesRes.success && modulesRes.result) {
                // Apenas os módulos liberados no plano principal.
                setSystemModules(modulesRes.result.filter(m => m.key in parent))
            }

            if (isEditing && id) {
                const planRes = await getSubPlan(parseInt(id, 10))
                if (planRes.success && planRes.result) {
                    const p = planRes.result
                    setName(p.name)
                    setDescription(p.description)
                    setPrice(formatCentsToDisplay(p.price))
                    setRecurrence(p.recurrence || 'MONTHLY')
                    const f = p.features || {}
                    setAllocations({
                        users: f.users ? String(f.users) : '',
                        teleconsultationMinutes: f.teleconsultationMinutes
                            ? String(f.teleconsultationMinutes)
                            : '',
                        monthlySignDocuments: f.monthlySignDocuments
                            ? String(f.monthlySignDocuments)
                            : '',
                        monthlyFiscalNotes: f.monthlyFiscalNotes
                            ? String(f.monthlyFiscalNotes)
                            : '',
                    })
                    setSelectedModules(p.additionalModules || {})
                } else {
                    toast.error('Plano não encontrado')
                    navigate('/administrative/resales')
                    return
                }
            }
        } catch {
            toast.error('Erro ao carregar dados da revenda')
        } finally {
            setLoadingPage(false)
        }
    }

    const handleAllocation = (field: AllocationKey, value: string) => {
        if (value === '') {
            setAllocations(prev => ({ ...prev, [field]: '' }))
            return
        }
        // Recursos ilimitados: sem clamp por pool. Só sanitiza (>= 0).
        let parsed = parseInt(value, 10)
        if (isNaN(parsed) || parsed < 0) parsed = 0
        setAllocations(prev => ({ ...prev, [field]: String(parsed) }))
    }

    // Preço mínimo do plano: calculado no backend (o front não conhece o
    // multiplicador nem o preço unitário). Recalcula com debounce ao alterar as
    // quantidades.
    useEffect(() => {
        const features: Record<string, number> = {}
        for (const { key } of ALLOCATION_FIELDS) {
            const qty = parseInt(allocations[key] || '0', 10) || 0
            if (qty > 0) features[key] = qty
        }
        if (Object.keys(features).length === 0) {
            setMinPriceCents(0)
            return
        }
        const handle = setTimeout(async () => {
            const res = await getResaleMinimumPrice(features)
            if (res.success && res.result)
                setMinPriceCents(res.result.minPriceCents)
        }, 400)
        return () => clearTimeout(handle)
    }, [allocations])

    const priceCents = parseCurrencyToCents(price)
    const belowMinimum = minPriceCents > 0 && priceCents < minPriceCents

    // Resources que o plano pai libera para um módulo. parentModules[key] === []
    // significa "todos os resources do módulo".
    const parentAllowsAll = (modKey: string): boolean =>
        (parentModules[modKey]?.length ?? 0) === 0

    const allowedResources = (mod: SystemModule) =>
        parentAllowsAll(mod.key)
            ? mod.resources
            : mod.resources.filter(r => parentModules[mod.key].includes(r.key))

    const allowedKeys = (mod: SystemModule): string[] =>
        allowedResources(mod).map(r => r.key)

    const toggleModule = (mod: SystemModule) => {
        setSelectedModules(prev => {
            const next = { ...prev }
            if (mod.key in next) {
                delete next[mod.key]
            } else {
                // Ao incluir: se o pai libera tudo, [] (todos); senão, grava os
                // resources liberados explicitamente (evita conceder além do pai).
                next[mod.key] = parentAllowsAll(mod.key) ? [] : allowedKeys(mod)
            }
            return next
        })
    }

    const isResourceSelected = (modKey: string, resKey: string): boolean => {
        const sel = selectedModules[modKey]
        if (!sel) return false
        if (sel.length === 0) return true // [] = todos
        return sel.includes(resKey)
    }

    const toggleResource = (mod: SystemModule, resKey: string) => {
        setSelectedModules(prev => {
            const sel = prev[mod.key]
            if (!sel) return prev
            const allowed = allowedKeys(mod)
            // Normaliza [] (todos) para a lista explícita antes de alternar.
            const current =
                sel.length === 0 ? [...allowed] : sel.filter(k => allowed.includes(k))
            const nextSel = current.includes(resKey)
                ? current.filter(k => k !== resKey)
                : [...current, resKey]

            const next = { ...prev }
            if (nextSel.length === 0) {
                // Sem resource selecionado: desmarca o módulo inteiro.
                delete next[mod.key]
            } else if (nextSel.length === allowed.length && parentAllowsAll(mod.key)) {
                // Todos selecionados e o pai libera tudo: pode colapsar para [].
                next[mod.key] = []
            } else {
                next[mod.key] = nextSel
            }
            return next
        })
    }

    const moduleResourceCount = (mod: SystemModule): number => {
        const sel = selectedModules[mod.key]
        if (!sel) return 0
        return sel.length === 0 ? allowedKeys(mod).length : sel.length
    }

    const toggleExpand = (key: string) => {
        setExpandedModules(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name.trim() || !description.trim()) {
            toast.warning('Preencha nome e descrição.')
            return
        }
        if (belowMinimum) {
            toast.warning(
                `O valor do plano não pode ser menor que o mínimo de revenda (${formatCentsToDisplay(
                    minPriceCents
                )}).`
            )
            return
        }

        const features: Record<string, any> = {}
        for (const { key } of ALLOCATION_FIELDS) {
            if (allocations[key]) features[key] = parseInt(allocations[key], 10)
        }

        const data: SubPlanData = {
            name: name.trim(),
            description: description.trim(),
            price: parseCurrencyToCents(price),
            recurrence,
            features,
            tierId: null,
            additionalModules: selectedModules,
            excludedModules: [],
        }

        setLoading(true)
        try {
            const res = isEditing
                ? await updateSubPlan(parseInt(id!, 10), data)
                : await createSubPlan(data)
            if (res.success) {
                toast.success(isEditing ? 'Plano atualizado' : 'Plano criado')
                navigate('/administrative/resales')
            } else {
                toast.error(res.error || 'Erro ao salvar plano')
            }
        } catch {
            toast.error('Erro ao salvar plano')
        } finally {
            setLoading(false)
        }
    }

    if (loadingPage) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/administrative/resales')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdStorefront className="text-2xl text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing
                            ? `Editar plano${name ? ` - ${name}` : ''}`
                            : 'Novo plano'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                {/* Links de cadastro (somente edição). Vários links vivos ao
                    mesmo tempo, um por cliente. */}
                {isEditing && id && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <RegistrationLinksPanel
                            onList={() =>
                                getSubPlanRegistrationLinks(parseInt(id, 10))
                            }
                            onCreate={(data) =>
                                createSubPlanRegistrationLink(
                                    parseInt(id, 10),
                                    data
                                )
                            }
                            onRevoke={(linkId) =>
                                revokeSubPlanRegistrationLink(
                                    parseInt(id, 10),
                                    linkId
                                )
                            }
                        />
                    </div>
                )}

                {/* Dados do plano */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Dados do plano
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text">Nome</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Ex: Plano Clínica"
                            />
                        </div>
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text">Descrição</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Breve descrição do plano"
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Valor da Assinatura
                                </span>
                            </label>
                            <input
                                type="text"
                                className={`input input-bordered w-full ${
                                    belowMinimum ? 'input-error' : ''
                                }`}
                                value={price}
                                onChange={e =>
                                    setPrice(currencyFormat(e.target.value))
                                }
                                placeholder="R$ 0,00"
                            />
                            {minPriceCents > 0 && (
                                <label className="label py-1">
                                    <span
                                        className={`label-text-alt ${
                                            belowMinimum
                                                ? 'text-error'
                                                : 'text-base-content/50'
                                        }`}
                                    >
                                        Mínimo de revenda:{' '}
                                        {formatCentsToDisplay(minPriceCents)}
                                        {belowMinimum
                                            ? ' — valor abaixo do mínimo'
                                            : ''}
                                    </span>
                                </label>
                            )}
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Recorrência</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={recurrence}
                                onChange={e => setRecurrence(e.target.value)}
                            >
                                <option value="MONTHLY">Mensal</option>
                                <option value="YEARLY">Anual</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Alocação de recursos */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Alocação de Recursos
                    </h2>
                    <p className="text-xs text-base-content/50 mb-4">
                        Quantidades ilimitadas. O preço do plano não pode ser
                        menor que o mínimo calculado abaixo. Deixe em branco para
                        não alocar.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {ALLOCATION_FIELDS.map(({ key, label }) => {
                            return (
                                <div key={key} className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">
                                            {label}
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="input input-bordered w-full"
                                        value={allocations[key]}
                                        onChange={e =>
                                            handleAllocation(
                                                key,
                                                e.target.value
                                            )
                                        }
                                        placeholder="Ex: 100"
                                    />
                                </div>
                            )
                        })}
                    </div>
                    {minPriceCents > 0 && (
                        <div className="mt-4 flex items-center justify-between rounded-lg bg-base-200 px-4 py-3">
                            <span className="text-sm text-base-content/70">
                                Preço mínimo do plano
                            </span>
                            <span className="text-base font-bold text-secondary">
                                {formatCentsToDisplay(minPriceCents)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Módulos */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1 flex items-center gap-2">
                        <MdExtension className="text-secondary" />
                        Módulos
                    </h2>
                    <p className="text-xs text-base-content/50 mb-4">
                        Apenas os módulos e recursos disponíveis no seu plano
                        principal são exibidos. Expanda um módulo para escolher
                        recursos específicos.
                    </p>
                    {systemModules.length === 0 ? (
                        <p className="text-sm text-base-content/50">
                            Nenhum módulo disponível no seu plano.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 gap-2">
                            {systemModules.map(mod => {
                                const selected = mod.key in selectedModules
                                const expanded = expandedModules.has(mod.key)
                                const resources = allowedResources(mod)
                                return (
                                    <div
                                        key={mod.key}
                                        className={`rounded-lg border transition-all ${
                                            selected
                                                ? 'border-secondary/50 bg-secondary/5'
                                                : 'border-base-300'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 p-3">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm checkbox-secondary"
                                                checked={selected}
                                                onChange={() => toggleModule(mod)}
                                            />
                                            <div
                                                className="flex-1 flex items-center justify-between cursor-pointer"
                                                onClick={() => toggleExpand(mod.key)}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium">
                                                        {mod.label}
                                                    </span>
                                                    {selected && resources.length > 0 && (
                                                        <span className="badge badge-xs badge-secondary">
                                                            {moduleResourceCount(mod)}/
                                                            {resources.length}
                                                        </span>
                                                    )}
                                                </div>
                                                {resources.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            toggleExpand(mod.key)
                                                        }}
                                                    >
                                                        {expanded ? (
                                                            <MdExpandLess className="text-lg" />
                                                        ) : (
                                                            <MdExpandMore className="text-lg" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {expanded && resources.length > 0 && (
                                            <div className="px-3 pb-3 pt-0">
                                                <div className="ml-8 grid grid-cols-2 md:grid-cols-3 gap-2">
                                                    {resources.map(res => (
                                                        <label
                                                            key={res.key}
                                                            className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all text-sm ${
                                                                isResourceSelected(mod.key, res.key)
                                                                    ? 'bg-secondary/10'
                                                                    : 'hover:bg-base-200'
                                                            } ${!selected ? 'opacity-40 pointer-events-none' : ''}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox checkbox-xs checkbox-secondary"
                                                                checked={isResourceSelected(mod.key, res.key)}
                                                                onChange={() => toggleResource(mod, res.key)}
                                                                disabled={!selected}
                                                            />
                                                            <span>{res.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                <div className="flex gap-2 pt-2">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1"
                        onClick={() => navigate('/administrative/resales')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary flex-1"
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="loading loading-spinner"></span>
                        ) : isEditing ? (
                            'Salvar'
                        ) : (
                            'Criar plano'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
